import React from "react";
// Customizable Area Start
import { createTheme, ThemeProvider, styled } from "@mui/material/styles";
import { Box, Card, CardContent, Chip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

export const theme = createTheme({
  typography: {
    fontFamily: "Montserrat !important",
    h1: {
      fontSize: "24px",
      fontWeight: "600",
      lineHeight: "29.26px",
      color: "#0F172A",
    },
    h3: {
      fontSize: "28px",
      fontWeight: "600",
      lineHeight: "32px",
      color: "#0F172A",
    },
    h4: {
      fontSize: "38px",
      fontWeight: "500",
      lineHeight: "43.68px",
      color: "#0F172A",
    },
    h5: {
      fontSize: "12px",
      fontWeight: "400",
      lineHeight: "18.72px",
      color: "#0F172A",
    },
    h6: {
      fontSize: "14px",
      fontWeight: "300",
      lineHeight: "18.72px",
      color: "#0F172A",
    },
    body1: {
      fontSize: "14px",
      fontWeight: "400",
      lineHeight: "20px",
      color: "#64748B",
    },
    body2: {
      fontSize: "16px",
      fontWeight: "500",
      lineHeight: "24px",
      color: "#64748B",
    },
  },
});

import SubscriptionPlansController, {
  Props,
  configJSON,
  IFeature,
  ISubscriptionPlan,
} from "./SubscriptionPlansController.web";
import NavigationMenu from "../../../blocks/navigationmenu/src/NavigationMenu.web";
import AppFooter from "../../../components/src/AppFooter.web";
import { bgImg } from "./assets";
import FullPageLoader from "../../../components/src/FullPageLoader.web";
import CustomConfirmationDialog from "../../../components/src/CustomConfirmationDialog.web";
import CustomInfoDialog from "../../../components/src/CustomInfoDialog.web";
import CustomButton from "../../../components/src/CustomButton.web";
import GoogleAd from "../../../components/src/GoogleAd.web";
// Customizable Area End

export default class SubscriptionPlans extends SubscriptionPlansController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  renderPlan(plan: ISubscriptionPlan) {
    const {
      id,
      attributes: { name, price, is_plan_used },
    } = plan;
    const { activePlan, isChangingPlan } = this.state;
    const { description, duration, features, shortDescription } =
      configJSON.PLAN_DETAILS[name];
    const isActivePlan = activePlan
      ? activePlan.attributes.subscription.id === Number(id)
      : false;
    const buttonText = isActivePlan
      ? configJSON.yourCurrentPlanText
      : configJSON.getPlanText;
    const isPlanNotAvailable =
      !isActivePlan && name === configJSON.freeText && is_plan_used;

    return (
      <StyledCard key={id} data-test-id="planCard">
        <CardContent>
          <Box>
            <Chip label={name} className="planTitle" />
            <Typography variant="body1" className="description">
              {shortDescription}
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="h4">{`${"\u20B9"} ${price}`}</Typography>
            <Typography variant="h5">{duration}</Typography>
          </Box>
          <Typography variant="body1" className="description">
            {description}
          </Typography>
          <Box className="featureContainer">
            {features.map((feature: IFeature, index: number) => (
              <Box key={index} className="featureItem">
                {feature.available ? (
                  <CheckIcon color="success" className="featureIcon" />
                ) : (
                  <CloseIcon color="error" className="featureIcon" />
                )}
                <Typography variant="h6" className="featureText">
                  {feature.text}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box className="btnWrapper">
            <CustomButton
              sx={{
                marginTop: 3,
                fontSize: "14px",
                width: "260px",
                height: "44px",
                "&:hover": {
                  color: "#fff",
                },
              }}
              data-test-id="get-plan"
              customVariant={isActivePlan ? "filled" : "contained"}
              disabled={isChangingPlan || isPlanNotAvailable}
              onClick={() => this.handleChangePlan(id, name, isActivePlan)}
            >
              {buttonText}
            </CustomButton>
          </Box>
        </CardContent>
      </StyledCard>
    );
  }
  // Customizable Area End

  render() {
    // Customizable Area Start
    const {
      isLoading,
      isChangingPlan,
      subscriptionPlans,
      upgradingPlanId,
      isUpgradeDialog,
      isInfoDialog,
      infoType,
      infoMessage,
      infoDescription,
    } = this.state;
    const isFullPageLoading = isLoading || isChangingPlan;
    // Customizable Area End
    return (
      // Customizable Area Start
      <ThemeProvider theme={theme}>
        <FullPageLoader isLoading={isFullPageLoading} />
        <NavigationMenu navigation={this.props.navigation} id={""} />
        <Wrapper>
          <Typography variant="h1" data-test-id="pageTitle" gutterBottom>
            {configJSON.chooseYourPlanText}
          </Typography>
          <Typography
            whiteSpace="pre-line"
            textAlign="center"
            variant="body2"
            gutterBottom
          >
            {configJSON.selectPlanDescriptionText}
          </Typography>
          <PlansContainer>
            {subscriptionPlans.map((plan) => this.renderPlan(plan))}
          </PlansContainer>
          <GoogleAd responsive format="auto" sx={{ mt: 6 }} slot="1440654926" />
          <CustomInfoDialog
            data-test-id="subscriptionSuccess"
            isOpen={isInfoDialog}
            infoType={infoType}
            title={infoMessage}
            description={infoDescription}
            handleClose={this.handleCloseInfoDialog}
          />
          <CustomConfirmationDialog
            data-test-id="subscriptionUpgrade"
            isOpen={isUpgradeDialog}
            title={configJSON.upgradePlanText}
            subTitle={configJSON.upgradePlanDescriptionText}
            actionBtnTitle={configJSON.confirmText}
            handleClose={this.handleCloseUpgradeDialog}
            handleAction={() => this.handleUpgradePlanApiCall(upgradingPlanId)}
          />
        </Wrapper>
        <AppFooter navigation={this.props.navigation} />
      </ThemeProvider>
      // Customizable Area End
    );
  }
}

// Customizable Area Start
const StyledCard = styled(Card)`
  flex: 1;
  min-width: 330px;
  max-width: 360px;
  border: 1.5px solid #652784;
  padding: 24px 15px;
  border-radius: 12px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease,
    background-color 0.3s ease;
  cursor: pointer;

  .planTitle {
    padding: 5px 0px;
    max-width: 110px;
    font-size: 20px;
    font-weight: 600;
    line-height: 24.96px;
    border-radius: 4px;
    margin-bottom: 16px;
  }

  .description {
    color: #64748b;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0px;
    vertical-align: bottom;
    margin-bottom: 15px;
  }

  .featureContainer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
  }

  .featureItem {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .featureIcon {
    width: 12px;
    height: 12px;
  }

  .featureText {
    color: #0f172a;
  }

  .btnWrapper {
    display: flex;
    justify-content: center;
  }

  &:hover {
    transform: translateY(-38.51px);
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.2);
    background-image: url(${bgImg});
    color: #fff;

    & .planTitle {
      color: #652784;
      background-color: #fff;
    }

    & p,
    h4,
    h5,
    h6 {
      color: #fff;
    }
  }
`;

const Wrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #eff6ff;
  flex-direction: column;
  padding: 45.5px 0px 80px;
`;

const PlansContainer = styled(Box)`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  max-width: 1120px;
  justify-content: center;
  margin-top: 74px;
  padding: 0px 20px;
  box-sizing: border-box;
`;
// Customizable Area End
